'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { usePathname } from 'next/navigation';
import { NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';
import { useProfitSplit } from '@/components/global/ProfitSplitProvider';

const I18N: Record<string, {
  title: string;
  accountSize: string;
  profitRate: string;
  yourShare: string;
  upTo: string;
  perMonth: string;
}> = {
  en: {
    title: 'Estimate Your Profits!',
    accountSize: 'Account size',
    profitRate: 'Profit Rate',
    yourShare: 'Your Share',
    upTo: 'up to',
    perMonth: '/ month',
  },
  de: {
    title: 'Schätze deinen Gewinn!',
    accountSize: 'Kontogröße',
    profitRate: 'Gewinnrate',
    yourShare: 'Dein Anteil',
    upTo: 'bis zu',
    perMonth: '/ Monat',
  },
  br: {
    title: 'Estime seus Lucros!',
    accountSize: 'Tamanho da conta',
    profitRate: 'Taxa de lucro',
    yourShare: 'Seu ganho',
    upTo: 'até',
    perMonth: '/ mês',
  },
};

const ACCOUNT_SIZES = [
  { size: 5000, profitSharePercentage: 90 },
  { size: 10000, profitSharePercentage: 90 },
  { size: 25000, profitSharePercentage: 90 },
  { size: 50000, profitSharePercentage: 90 },
  { size: 100000, profitSharePercentage: 90 },
  { size: 200000, profitSharePercentage: 90 },
];

const MIN_PROFIT_RATE = 0;
const MAX_PROFIT_RATE = 10;

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    notation: 'standard',
    compactDisplay: 'long',
  }).format(Math.round(value));
}

export default function ProfitSlider() {
  const pathname = usePathname();
  const language = NON_DEFAULT_LANGUAGES.find(
    (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
  ) ?? DEFAULT_LANGUAGE;
  const t = I18N[language] ?? I18N[DEFAULT_LANGUAGE];

  const [selectedIndex, setSelectedIndex] = useState(3);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [profitRate, setProfitRate] = useState(4.3);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const profitSplit = useProfitSplit();
  const selectedAccount = ACCOUNT_SIZES[selectedIndex];
  const sharePercentage = profitSplit ?? selectedAccount.profitSharePercentage;
  const traderEarnings =
    ((selectedAccount.size * profitRate) / 100) * (sharePercentage / 100);
  const sliderPercent =
    ((profitRate - MIN_PROFIT_RATE) / (MAX_PROFIT_RATE - MIN_PROFIT_RATE)) * 100;

  // Spring-animated earnings counter
  const springEarnings = useSpring(traderEarnings, { stiffness: 80, damping: 18 });
  const animatedEarnings = useTransform(springEarnings, (v) => `$${formatCurrency(v)}`);

  useEffect(() => {
    springEarnings.set(traderEarnings);
  }, [traderEarnings, springEarnings]);

  const prev = useCallback(() => {
    setDirection(-1);
    setSelectedIndex((i) => (i === 0 ? ACCOUNT_SIZES.length - 1 : i - 1));
  }, []);

  const next = useCallback(() => {
    setDirection(1);
    setSelectedIndex((i) => (i === ACCOUNT_SIZES.length - 1 ? 0 : i + 1));
  }, []);

  const updateRateFromPointer = useCallback((clientX: number) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setProfitRate(
      Math.round(
        (MIN_PROFIT_RATE + ratio * (MAX_PROFIT_RATE - MIN_PROFIT_RATE)) * 10,
      ) / 10,
    );
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current) return;
      updateRateFromPointer('touches' in e ? e.touches[0].clientX : e.clientX);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [updateRateFromPointer]);

  return (
    <div className="bg-[#04271d] rounded-[0.25rem] pt-[4rem] pb-[5rem] px-[5.25rem] flex flex-col gap-[5.25rem] items-center relative overflow-hidden w-full max-w-[61.875rem]">
      {/* Title */}
      <p className="mb-0 font-geist font-medium text-[3rem] text-white text-center tracking-[-0.15rem] leading-[1.5]">
        {t.title}
      </p>

      <div className="flex flex-col gap-[4.25rem] items-center w-full">
        {/* Controls */}
        <div className="flex flex-col gap-[5rem] items-center w-full">
          {/* Account size */}
          <div className="flex flex-col gap-[1.5rem] items-center w-full">
            <p className="mb-0 font-geist-mono text-[2.25rem] text-white text-center leading-[1.5]">
              {t.accountSize}
            </p>
            <div className="flex items-center justify-between w-full bg-white/[0.13] rounded-[0.25rem] px-[1rem] py-[1.125rem] h-[7.625rem] overflow-hidden">
              <motion.button
                onClick={prev}
                whileTap={{ scale: 0.92 }}
                className="w-[5.5rem] h-[5.5rem] rounded-[0.25rem] bg-[#12f0b4] flex items-center justify-center flex-shrink-0"
                aria-label="Previous account size"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.75 9H14.25" stroke="#04271d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>

              <div className="relative flex-1 flex justify-center overflow-hidden h-full items-center">
                <AnimatePresence mode="popLayout" initial={false} custom={direction}>
                  <motion.p
                    key={selectedIndex}
                    custom={direction}
                    initial={{ y: direction * 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: direction * -24, opacity: 0 }}
                    transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                    className="mb-0 font-geist font-medium text-[2.75rem] text-white text-center leading-[1.5] tracking-[-0.138rem] whitespace-nowrap absolute"
                  >
                    ${new Intl.NumberFormat('en-US').format(selectedAccount.size)}
                  </motion.p>
                </AnimatePresence>
              </div>

              <motion.button
                onClick={next}
                whileTap={{ scale: 0.92 }}
                className="w-[5.5rem] h-[5.5rem] rounded-[0.25rem] bg-[#12f0b4] flex items-center justify-center flex-shrink-0"
                aria-label="Next account size"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 3.75V14.25M3.75 9H14.25" stroke="#04271d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>
            </div>
          </div>

          {/* Profit rate */}
          <div className="flex flex-col gap-[3rem] items-center w-full">
            <div className="flex flex-col gap-[1.5rem] items-center w-full">
              <p className="mb-0 font-geist-mono text-[2.25rem] text-white text-center leading-[1.5]">
                {t.profitRate}
              </p>
              <div className="bg-white/[0.13] rounded-[0.25rem] px-[1.875rem] py-[0.5rem] h-[5.375rem] w-[10.75rem] flex items-center justify-center">
                <motion.p
                  key={profitRate}
                  initial={{ opacity: 0.6, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.15 }}
                  className="mb-0 font-geist-mono font-medium text-[2.25rem] text-white text-center leading-[1.5] whitespace-nowrap tabular-nums"
                >
                  {profitRate.toFixed(1)}%
                </motion.p>
              </div>
            </div>

            {/* Slider track */}
            <div
              ref={sliderRef}
              className="relative w-full h-[2.5rem] cursor-pointer flex items-center"
              onMouseDown={(e) => {
                isDragging.current = true;
                updateRateFromPointer(e.clientX);
              }}
              onTouchStart={(e) => {
                isDragging.current = true;
                updateRateFromPointer(e.touches[0].clientX);
              }}
              onClick={(e) => updateRateFromPointer(e.clientX)}
            >
              <div className="absolute w-full h-[1.5rem] rounded-[0.25rem] bg-white/[0.27]" />
              <motion.div
                className="absolute left-0 h-[1.5rem] rounded-[0.25rem] bg-[#12f0b4]"
                animate={{ width: `${sliderPercent}%` }}
                transition={{ duration: 0.07, ease: 'linear' }}
              />
              <motion.div
                className="absolute -translate-x-1/2 w-[2.5rem] h-[2.5rem] rounded-full bg-white shadow-md cursor-grab active:cursor-grabbing"
                animate={{ left: `${sliderPercent}%` }}
                transition={{ duration: 0.07, ease: 'linear' }}
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <svg
          className="w-full h-auto shrink-0"
          height="1"
          viewBox="0 0 495 1"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 0.5H495" stroke="white" strokeOpacity="0.12" strokeDasharray="8 8" />
        </svg>

        {/* Result */}
        <div className="flex flex-col gap-[3rem] items-center">
          <p className="mb-0 font-geist-mono text-[2.25rem] text-white text-center leading-[1.5]">
            {t.yourShare}{' '}
            <span className="font-bold">
              ({profitSplit != null && `${t.upTo} `}
              {sharePercentage}%)
            </span>
          </p>
          <div className="flex items-end gap-[0.625rem] pl-[3rem]">
            <motion.p className="mb-0 font-geist text-[6.25rem] text-white leading-[1.5] tracking-[-0.3125rem] whitespace-nowrap tabular-nums">
              {animatedEarnings}
            </motion.p>
            <p className="mb-0 text-[2rem] text-white/50 leading-[1.5] pb-[0.375rem] whitespace-nowrap">
              {t.perMonth}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
